import { useState, useEffect } from 'react';
import { useGetMailSettingsQuery, useSaveMailSettingsMutation } from '../api';
import { MailSettings, ForwardingPeriodicity, ForwardingType } from '../api/mails/types';
import { useAlert } from './useAlert';
import { useTranslation } from './useTranslation';
import { useDebouncedCallback } from 'use-debounce';

interface UpdateSettingsParams {
    scan_content?: boolean;
    forwarding_periodicity?: ForwardingPeriodicity;
    forwarding_type?: ForwardingType;
}

/**
 * Хук для управления настройками почты
 * Автоматически сохраняет изменения при их внесении
 */
export const useMailsSettings = () => {
    const { t } = useTranslation();
    const { success, error: showError } = useAlert();
    const { data: mailSettingsData, isLoading: isMailsLoading } = useGetMailSettingsQuery();
    const [saveMailSettings, { isLoading: isSaving }] = useSaveMailSettingsMutation();

    // Получаем настройки из API
    const apiSettings = mailSettingsData?.settings;

    // Локальное состояние для оптимистичного обновления UI
    const [settings, setSettings] = useState<MailSettings | undefined>(apiSettings);

    // Синхронизируем локальное состояние с данными из API
    useEffect(() => {
        if (apiSettings) {
            setSettings(apiSettings);
        }
    }, [apiSettings]);

    // Функция сохранения настроек
    const saveSettings = async (newSettings: MailSettings) => {
        // Не сохраняем, если данных из API нет
        if (!apiSettings) {
            return;
        }

        try {
            await saveMailSettings(newSettings).unwrap();
            success(t.pages.mail.settingsSaved);
        } catch (error) {
            showError(t.pages.mail.saveError);
        }
    };

    // Debounced функция для автоматического сохранения
    const debouncedSave = useDebouncedCallback(
        (newSettings: MailSettings) => {
            saveSettings(newSettings);
        },
        500, // Задержка 500мс
    );

    // Общая функция для обновления настроек
    const updateSettings = (params: UpdateSettingsParams) => {
        // Не обновляем, если данных из API нет
        if (!settings) {
            return;
        }

        const newSettings: MailSettings = {
            scan_content: params.scan_content ?? settings.scan_content,
            forwarding_periodicity: params.forwarding_periodicity ?? settings.forwarding_periodicity,
            forwarding_type: params.forwarding_type ?? settings.forwarding_type,
        };

        if (newSettings.forwarding_periodicity === 'off') {
            newSettings.forwarding_type = 'ordinary';
        }

        // Сразу обновляем локальное состояние для оптимистичного обновления UI
        setSettings(newSettings);

        // Вызываем debouncedSave с обновленными значениями
        debouncedSave(newSettings);
    };

    return {
        settings,
        updateSettings,
        isLoading: isMailsLoading,
        isSaving,
    };
};
